import { ReportDefinitionApiError } from 'src/components/star-grid/utils/reportDefinitionError';
import { useCallback, useMemo, useRef } from 'react';
import { useDefinitionResource } from 'src/components/star-grid/hooks/useDefinitionResource';
import type { ReportParameter, ReportParameterOption } from 'src/components/star-grid/reportDefinition.types';
import type { PnlHierarchyTree } from 'src/components/star-grid/starGridTypes';
import { getHierarchyTree } from 'src/components/star-grid/services/starGridService';
import { parameterOptions, selectedParameterOptions, uniqueParameterOptions } from 'src/components/star-grid/utils/reportDefinitionSelection';

interface ParameterChoices {
  readonly options: readonly ReportParameterOption[];
  readonly tree?: PnlHierarchyTree;
}
type HierarchyLoader = (id: string, signal: AbortSignal) => Promise<PnlHierarchyTree | undefined>;
export const useReportParameter = (
  parameter: ReportParameter,
  values: readonly string[],
  enabled = true,
  loadHierarchy: HierarchyLoader = getHierarchyTree
) => {
  // Keyed by what the load reads, not the parameter object: the bar rebuilds its parameters whenever a
  // chip label resolves, and a new fetcher per rebuild asked for the same tree twice.
  const latest = useRef(parameter);
  latest.current = parameter;
  const loadKey = [parameter.hierarchy_id ?? '', ...(parameter.options ?? []).map((option) => option.value), '|', ...parameter.default].join(
    '\u0001'
  );
  const fetcher = useCallback(
    async (signal: AbortSignal): Promise<ParameterChoices> => {
      const parameter = latest.current;
      const fallback = { options: parameter.options ?? parameter.default.map((value) => ({ value, label: value })) };
      if (!enabled || !parameter.hierarchy_id) return fallback;
      let tree: PnlHierarchyTree | undefined;
      try {
        tree = await loadHierarchy(parameter.hierarchy_id, signal);
      } catch (error) {
        // A rollup-latent or unflattened hierarchy the tree endpoint cannot serve (404) degrades to
        // the parameter's own default rather than an error — the dropdown still shows its value.
        if (!(error instanceof ReportDefinitionApiError && error.status === 404)) throw error;
        return fallback;
      }
      if (!tree) return fallback;
      if (tree.hierarchy_id !== parameter.hierarchy_id) throw new Error('Unexpected report hierarchy');
      return { tree, options: uniqueParameterOptions(parameterOptions(tree.members)) };
    },
    [loadKey, enabled, loadHierarchy]
  );
  const resource = useDefinitionResource(fetcher);
  // A list stated up front needs no round trip, so it never blanks while a refetch is in flight.
  const options = resource.data?.options ?? (parameter.hierarchy_id ? undefined : parameter.options);
  const selected = useMemo(() => selectedParameterOptions(parameter, values, options ?? []), [parameter, values, options]);
  return {
    ...resource,
    loading: resource.loading && !options,
    data: options,
    tree: resource.data?.tree,
    selected,
    usesDefault: !parameter.hierarchy_id && !parameter.options
  };
};
